import { getCookieDomain } from '../../core/util/env';

/**
 * The cross-merchant source of truth: a single cookie scoped to the registrable Vipps/MobilePay
 * domain (e.g. `.vipps.no`). It is only readable/writable once the Storage Access API has granted
 * access to unpartitioned cookies (see storageAccess.ts).
 *
 * POC note: the payload is base64-encoded JSON, mirroring the existing remember-me-storage format.
 * Production hardening should replace the raw phone number with a server-issued opaque token.
 */
const COOKIE_NAME = 'vipps-xdomain-remember-me';
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

export interface CookieIdentity {
  country?: string;
  number?: string;
}

export const read = (): CookieIdentity | null => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!entry) {
    return null;
  }
  try {
    const raw = decodeURIComponent(entry.slice(COOKIE_NAME.length + 1));
    return JSON.parse(atob(raw)) as CookieIdentity;
  } catch {
    return null;
  }
};

export const write = (identity: CookieIdentity): void => {
  if (typeof document === 'undefined') {
    return;
  }
  const value = encodeURIComponent(btoa(JSON.stringify(identity)));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // SameSite=None is required so the cookie is available in the cross-site (embedded) context.
  document.cookie = `${COOKIE_NAME}=${value}; Domain=${getCookieDomain()}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=None${secure}`;
};

export const clear = (): void => {
  if (typeof document === 'undefined') {
    return;
  }
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=; Domain=${getCookieDomain()}; Path=/; Max-Age=0; SameSite=None${secure}`;
};
